/**
 * Построение фундаментального многоугольника гиперболической карты и
 * классификация типа геометрии.
 *
 * Карта задаётся правильным многоугольником с `n` сторонами и схемой склейки.
 * Базовый и обязательный случай — правильный гиперболический восьмиугольник
 * рода 2 («крендель») со склейкой `abABcdCD`.
 *
 * Циркумрадиус (гиперболический) правильного `{p,q}`-многоугольника:
 * `cosh(R) = cot(π/p)·cot(π/q)`. Евклидов радиус вершин в модели Пуанкаре
 * `r_disk = tanh(R/2)`. Вершины: `v_k = r_disk · e^{i·(2πk/p + φ0)}`.
 *
 * ПРИМЕЧАНИЕ. В design.md приведена формула `cosh(R) = cos(π/p)/sin(π/q)`,
 * однако она даёт внутренний угол `π/2` (сумма `4π`) для `{8,8}`, что
 * противоречит Требованию 2.2 (углы `π/4`, сумма `2π`). Здесь используется
 * корректная формула `cosh(R) = cot(π/p)·cot(π/q)`, выводимая из
 * гиперболической теоремы косинусов и дающая ровно сумму углов `2π` для
 * восьмиугольника рода 2 (проверено численно).
 *
 * Тип геометрии определяется сравнением суммы внутренних углов с евклидовой
 * `(n−2)π`: меньше → гиперболическая, равна → евклидова, больше → сферическая.
 *
 * Все функции чистые и детерминированные, без DOM.
 *
 * Требования: 2.1, 2.2, 2.3, 2.4
 */

import type {
  Complex,
  FundamentalPolygon,
  GeometryType,
  SidePairingSpec,
} from './map-types.js';
import { fromPolar, argument } from './complex.js';
import { apply, translateToOrigin } from './mobius.js';

/** Допуск при сравнении суммы углов с евклидовой границей (радианы). */
export const ANGLE_TOLERANCE = 1e-9;

/**
 * Допуск классификации евклидовой/сферической геометрии по сумме внутренних
 * углов (радианы). Согласно Требованиям 6.1, 6.2, 6.8 сумма сравнивается с
 * евклидовой границей `(n−2)π` и сферическим максимумом `nπ` с абсолютным
 * отклонением не более `1·10⁻⁶`.
 */
export const EUCLIDEAN_ANGLE_TOLERANCE = 1e-6;

/**
 * Допуск сравнения показателя кривизны `1/p + 1/q` с евклидовой границей `1/2`
 * (Требования 1.1–1.3). Для целых `p, q ≥ 3` разность `s − 1/2` либо равна нулю
 * с точностью машинного эпсилон (точные евклидовы пары, например `{4,4}`,
 * `{6,3}`), либо отстоит от нуля существенно больше этого допуска, поэтому
 * трёхстороннее сравнение устойчиво.
 */
export const CURVATURE_TOLERANCE = 1e-9;

/** Минимальное число сторон для евклидовой/сферической карты (Требование 6). */
export const MIN_GEOMETRY_SIDES = 4;

/** Максимальное число сторон для евклидовой/сферической карты (Требование 6). */
export const MAX_GEOMETRY_SIDES = 12;

/** Имя пресета «крендель рода 2». */
export const GENUS2_PRESET = 'genus2';

/**
 * Схема склейки восьмиугольника рода 2 (заглавные — обратные стороны).
 *
 * Слово `abABcdCD` соответствует коммутаторному соотношению фуксовой группы
 * рода 2 `[a,b][c,d] = 1` и задаёт порядок обхода 8 сторон восьмиугольника:
 * сторона 0 → `a`, 1 → `b`, 2 → `A` (обратная `a`), 3 → `B` (обратная `b`),
 * 4 → `c`, 5 → `d`, 6 → `C` (обратная `c`), 7 → `D` (обратная `d`).
 */
export const GENUS2_PAIRING_WORD = 'abABcdCD';

/**
 * Гиперболический циркумрадиус правильного `{p,q}`-многоугольника.
 * `cosh(R) = cot(π/p)·cot(π/q)`, откуда `R = arcosh(cot(π/p)·cot(π/q))`.
 * Формула выводится из гиперболической теоремы косинусов для равнобедренного
 * треугольника «центр — две соседние вершины» и гарантирует внутренний угол
 * `2π/q` при каждой вершине.
 *
 * Для существования гиперболического многоугольника требуется `coshR > 1`
 * (эквивалентно `1/p + 1/q < 1/2`); иначе конфигурация не гиперболическая.
 *
 * @param p число сторон многоугольника (`p ≥ 3`)
 * @param q число многоугольников, сходящихся в каждой вершине замощения (`q ≥ 3`)
 * @returns гиперболическое расстояние `R` от центра до вершины
 */
export function regularHyperbolicCircumradius(p: number, q: number): number {
  if (!Number.isFinite(p) || !Number.isFinite(q) || p < 3 || q < 3) {
    throw new Error(`Invalid Schläfli parameters {${p},${q}}: expected p ≥ 3 and q ≥ 3`);
  }
  const coshR = (Math.cos(Math.PI / p) * Math.cos(Math.PI / q)) /
    (Math.sin(Math.PI / p) * Math.sin(Math.PI / q));
  if (coshR <= 1 + ANGLE_TOLERANCE) {
    throw new Error(
      `{${p},${q}} is not a hyperbolic tiling (requires 1/p + 1/q < 1/2, cosh(R) = ${coshR})`,
    );
  }
  return Math.acosh(coshR);
}

/**
 * Евклидов радиус вершин правильного `{p,q}`-многоугольника в модели Пуанкаре.
 * `r_disk = tanh(R/2)`, где `R` — гиперболический циркумрадиус.
 */
export function regularHyperbolicDiskRadius(p: number, q: number): number {
  return Math.tanh(regularHyperbolicCircumradius(p, q) / 2);
}

/**
 * Евклидов циркумрадиус вершин плоского правильного многоугольника в
 * координатах модели. Плоская геометрия безмасштабна (любой положительный
 * радиус эквивалентен с точностью до зума вида), поэтому радиус задаётся
 * соглашением. Значение `1.0` — единичная описанная окружность в плоскости
 * модели; `euclideanGeometry.project` — тождество, поэтому вершины сразу
 * являются нормализованными клип-координатами. По величине сопоставимо с
 * дисковыми радиусами гиперболической (`tanh(R/2)`) и сферической (`tan(R/2)`)
 * ветвей для поддерживаемых `{p,q}`. Масштабирование к экрану выполняет
 * контроллер вида (`map-view-controller`).
 */
export const EUCLIDEAN_MODEL_RADIUS = 1;

/**
 * Сферический циркумрадиус правильного `{p,q}`-многоугольника:
 * `cos R = cot(π/p)·cot(π/q)` (сферический аналог гиперболической формулы,
 * из теоремы косинусов прямоугольного сферического треугольника с углами
 * `π/p`, `π/q`, `π/2`). Для сферической конфигурации `1/p + 1/q > 1/2`, откуда
 * `cos R < 1` и `R ∈ (0, π/2)`.
 *
 * Для существования сферического многоугольника требуется `cos R < 1`
 * (эквивалентно `1/p + 1/q > 1/2`); иначе конфигурация не сферическая, и
 * функция бросает исключение (guard `cos R ≥ 1`).
 *
 * @param p число сторон многоугольника (`p ≥ 3`)
 * @param q число многоугольников, сходящихся в каждой вершине замощения (`q ≥ 3`)
 * @returns сферическое расстояние `R` от центра до вершины
 */
export function regularSphericalCircumradius(p: number, q: number): number {
  const cosR =
    (Math.cos(Math.PI / p) * Math.cos(Math.PI / q)) /
    (Math.sin(Math.PI / p) * Math.sin(Math.PI / q));
  if (cosR >= 1 - ANGLE_TOLERANCE) {
    throw new Error(
      `{${p},${q}} is not a spherical tiling (requires 1/p + 1/q > 1/2, cos(R) = ${cosR})`,
    );
  }
  return Math.acos(cosR);
}

/**
 * Стереографический радиус вершин сферического многоугольника: `r_disk = tan(R/2)`.
 * Центр многоугольника проецируется в начало координат (южный полюс), вершины —
 * на сферическом расстоянии `R` от центра.
 */
function sphericalDiskRadius(p: number, q: number): number {
  return Math.tan(regularSphericalCircumradius(p, q) / 2);
}

/**
 * Построить правильный гиперболический `{p,q}`-многоугольник в модели Пуанкаре.
 *
 * Вершины лежат на евклидовом радиусе `r_disk = tanh(R/2)` и равномерно
 * распределены по углу: `v_k = r_disk · e^{i·(2πk/p + φ0)}`, `k = 0..p−1`.
 * Внутренний угол при каждой вершине равен `2π/q`, поэтому сумма внутренних
 * углов равна `p · 2π/q`.
 *
 * @param p число сторон многоугольника (`p ≥ 3`)
 * @param q число многоугольников, сходящихся в каждой вершине (`q ≥ 3`)
 * @param phi0 угловое смещение первой вершины (радианы, по умолчанию `0`)
 * @param preset опциональное имя пресета для сохранения в конфигурации
 */
export function buildRegularHyperbolicPolygon(
  p: number,
  q: number,
  phi0 = 0,
  preset?: string,
): FundamentalPolygon {
  const rDisk = regularHyperbolicDiskRadius(p, q);
  const vertices: Complex[] = [];
  for (let k = 0; k < p; k++) {
    vertices.push(fromPolar(rDisk, (2 * Math.PI * k) / p + phi0));
  }
  const angleSum = (p * 2 * Math.PI) / q;
  return { sides: p, preset, vertices, angleSum };
}

/**
 * Построить правильный `{p,q}`-многоугольник для автоопределённой геометрии.
 *
 * Тип выбирается {@link detectGeometryType}. Во всех ветвях внутренний угол при
 * вершине равен `2π/q`, а сумма внутренних углов — `p·(2π/q)` (Требования 2.1,
 * 2.2); вершины равномерно распределены по углу: `v_k = r · e^{i(2πk/p + φ0)}`,
 * `k = 0..p−1`, и их число равно `p` (Требование 2.6).
 *
 * - гиперболическая — делегирует {@link buildRegularHyperbolicPolygon} (модель
 *   Пуанкаре, `r = tanh(R/2)`, `cosh R = cot(π/p)·cot(π/q)`) — путь неизменен
 *   (Требование 2.5);
 * - евклидова — плоский многоугольник, `r = EUCLIDEAN_MODEL_RADIUS`
 *   (Требование 2.3);
 * - сферическая — сферический циркумрадиус `cos R = cot(π/p)·cot(π/q)`, вершины
 *   на стереографическом радиусе `r_disk = tan(R/2)` (Требование 2.4).
 *
 * В не-гиперболических ветвях поле `preset` проставляется в результат явно,
 * чтобы обратный маппинг формы мог восстановить выбранный пресет.
 *
 * @param p число сторон многоугольника (`p ≥ 3`)
 * @param q число многоугольников, сходящихся в каждой вершине (`q ≥ 3`)
 * @param phi0 угловое смещение первой вершины (радианы, по умолчанию `0`)
 * @param preset опциональное имя пресета для сохранения в конфигурации
 */
export function buildRegularPolygon(
  p: number,
  q: number,
  phi0 = 0,
  preset?: string,
): FundamentalPolygon {
  const type = detectGeometryType(p, q);
  if (type === 'hyperbolic') {
    return buildRegularHyperbolicPolygon(p, q, phi0, preset);
  }

  // Евклидова ветвь — плоская окружность единичного радиуса; сферическая —
  // стереографический радиус tan(R/2).
  const rDisk =
    type === 'euclidean' ? EUCLIDEAN_MODEL_RADIUS : sphericalDiskRadius(p, q);

  const vertices: Complex[] = [];
  for (let k = 0; k < p; k++) {
    vertices.push(fromPolar(rDisk, (2 * Math.PI * k) / p + phi0));
  }
  const angleSum = (p * 2 * Math.PI) / q;
  return { sides: p, preset, vertices, angleSum };
}

/**
 * Пресет «крендель рода 2»: правильный гиперболический восьмиугольник `{8,8}`
 * со склейкой `abABcdCD`. Внутренний угол каждой вершины равен `π/4`, сумма
 * углов равна `2π`, что обеспечивает корректное замощение вокруг вершины.
 *
 * @param phi0 угловое смещение первой вершины (радианы, по умолчанию `0`)
 * @returns фундаментальный многоугольник и схема склейки
 */
export function buildGenus2Pretzel(
  phi0 = 0,
): { polygon: FundamentalPolygon; sidePairing: SidePairingSpec } {
  const polygon = buildRegularHyperbolicPolygon(8, 8, phi0, GENUS2_PRESET);
  const sidePairing: SidePairingSpec = { word: GENUS2_PAIRING_WORD };
  return { polygon, sidePairing };
}

/**
 * Внутренний угол многоугольника при вершине `vertex`, образованный
 * геодезическими к соседним вершинам `prev` и `next` (в радианах, из `[0, π]`).
 *
 * Вершина переносится в центр диска изометрией {@link translateToOrigin};
 * поскольку преобразование Мёбиуса конформно, а геодезические из центра —
 * прямые радиусы, искомый угол равен евклидовой разности аргументов образов
 * соседних вершин.
 */
export function computeVertexAngle(
  prev: Complex,
  vertex: Complex,
  next: Complex,
): number {
  const toOrigin = translateToOrigin(vertex);
  const prevImage = apply(toOrigin, prev);
  const nextImage = apply(toOrigin, next);
  let delta = argument(nextImage) - argument(prevImage);
  // Привести к [−π, π], затем взять абсолютный угол в [0, π].
  while (delta > Math.PI) delta -= 2 * Math.PI;
  while (delta < -Math.PI) delta += 2 * Math.PI;
  return Math.abs(delta);
}

/**
 * Сумма внутренних углов многоугольника, вычисленная геометрически по его
 * вершинам (в порядке обхода). Служит независимой проверкой аналитического
 * значения `angleSum`.
 *
 * @param vertices вершины многоугольника в порядке обхода (`≥ 3`)
 */
export function computeInteriorAngleSum(vertices: Complex[]): number {
  const n = vertices.length;
  if (n < 3) {
    throw new Error(`Polygon must have at least 3 vertices, got ${n}`);
  }
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];
    sum += computeVertexAngle(prev, curr, next);
  }
  return sum;
}

/**
 * Определить тип геометрии по символу Шлефли `{p,q}` через показатель кривизны
 * `s = 1/p + 1/q`, сравниваемый с `1/2` (Требования 1.1–1.3):
 *   `s < 1/2 − допуск` → `'hyperbolic'`
 *   `|s − 1/2| ≤ допуск` → `'euclidean'`
 *   `s > 1/2 + допуск` → `'spherical'`
 *
 * Единый источник решения о типе геометрии по `{p,q}` (используется и общим
 * построителем при выборе ветви, и формой админки для локализованных
 * сообщений). Чистая и детерминированная.
 *
 * Сравнение показателя кривизны с `1/2` алгебраически тождественно сравнению
 * суммы углов `p·(2π/q)` с евклидовой границей `(p−2)π`:
 *   `p·(2π/q) ⋛ (p−2)·π ⇔ 2/q ⋛ 1 − 2/p ⇔ 1/p + 1/q ⋛ 1/2`.
 * Поэтому результат совпадает с `classifyGeometry(p·2π/q, p)` (Требование 1.5).
 *
 * @param p число сторон многоугольника (целое `p ≥ 3`)
 * @param q число многоугольников, сходящихся в каждой вершине (целое `q ≥ 3`)
 * @throws Error если `p` или `q` нецелые, неконечные либо `< 3`
 */
export function detectGeometryType(p: number, q: number): GeometryType {
  if (
    !Number.isInteger(p) ||
    !Number.isInteger(q) ||
    p < 3 ||
    q < 3
  ) {
    throw new Error(
      `Invalid Schläfli parameters {${p},${q}}: expected integers p ≥ 3 and q ≥ 3`,
    );
  }
  const s = 1 / p + 1 / q;
  if (s < 0.5 - CURVATURE_TOLERANCE) return 'hyperbolic';
  if (Math.abs(s - 0.5) <= CURVATURE_TOLERANCE) return 'euclidean';
  return 'spherical';
}

/**
 * Классифицировать тип геометрии по сумме внутренних углов многоугольника.
 *
 * Сравнивается с евклидовой суммой `(n−2)π` и сферическим максимумом `nπ`
 * (допуск {@link EUCLIDEAN_ANGLE_TOLERANCE} = `1·10⁻⁶` радиан):
 * - сумма строго меньше `(n−2)π − допуск` → `'hyperbolic'` (гиперболическая
 *   карта, в частности восьмиугольник рода 2 с суммой `2π`);
 * - сумма равна `(n−2)π` в пределах допуска → `'euclidean'` (Требование 6.1);
 * - сумма в `((n−2)π, nπ]` сверх допуска → `'spherical'` (Требование 6.2).
 *
 * Для евклидовой и сферической классификации требуется чётное число сторон
 * `n` в диапазоне `[4, 12]` (Требования 6.1, 6.2). Если конфигурация не
 * попадает ни в один класс — нечётное `n`, `n` вне `[4, 12]` для
 * плоской/сферической суммы, либо сумма превышает сферический максимум `nπ`
 * сверх допуска — функция бросает ошибку с указанием причины (Требование 6.8).
 * Вызывающий код (валидатор геометрии) перехватывает ошибку и возвращает
 * сообщение об отклонении, сохраняя предыдущую валидную конфигурацию.
 *
 * @param angleSum сумма внутренних углов многоугольника (радианы)
 * @param sides число сторон `n`
 * @param tolerance допуск сравнения с границами классов геометрии
 * @throws Error если конфигурация не соответствует ни одному типу геометрии
 */
export function classifyGeometry(
  angleSum: number,
  sides: number,
  tolerance = EUCLIDEAN_ANGLE_TOLERANCE,
): GeometryType {
  const euclideanSum = (sides - 2) * Math.PI;

  // Гиперболическая: сумма строго меньше евклидовой границы (без ограничений
  // на чётность/диапазон сторон — сохраняет поведение существующих
  // гиперболических карт, в т.ч. восьмиугольника рода 2 с суммой 2π).
  if (angleSum < euclideanSum - tolerance) {
    return 'hyperbolic';
  }

  // Евклидова и сферическая геометрии допускают только чётное число сторон
  // в диапазоне [4, 12] (Требования 6.1, 6.2).
  const sidesValid =
    Number.isInteger(sides) &&
    sides % 2 === 0 &&
    sides >= MIN_GEOMETRY_SIDES &&
    sides <= MAX_GEOMETRY_SIDES;

  // Евклидова: сумма равна (n−2)π в пределах допуска.
  if (Math.abs(angleSum - euclideanSum) <= tolerance) {
    if (!sidesValid) {
      throw new Error(
        `Euclidean geometry requires an even number of sides in [${MIN_GEOMETRY_SIDES}, ${MAX_GEOMETRY_SIDES}], got n=${sides}`,
      );
    }
    return 'euclidean';
  }

  // Сферическая: сумма в ((n−2)π, nπ] сверх допуска.
  const sphericalMax = sides * Math.PI;
  if (angleSum <= sphericalMax + tolerance) {
    if (!sidesValid) {
      throw new Error(
        `Spherical geometry requires an even number of sides in [${MIN_GEOMETRY_SIDES}, ${MAX_GEOMETRY_SIDES}], got n=${sides}`,
      );
    }
    return 'spherical';
  }

  // Сумма превышает сферический максимум nπ — недопустимая конфигурация.
  throw new Error(
    `Angle sum ${angleSum} exceeds the spherical maximum nπ=${sphericalMax} for n=${sides}; configuration does not match any geometry type`,
  );
}
